<template>
  <b-tooltip
    ref="tooltip"
    :active="active"
    :type="type"
    :label="label"
    :delay="delay"
    :close-delay="closeDelay"
    :position="position"
    :triggers="triggers"
    :always="always"
    :square="square"
    :dashed="dashed"
    :multilined="multilined"
    :size="size"
    :append-to-body="appendToBody"
    :animated="animated"
    :animation="animation"
    :content-class="contentClass"
    :auto-close="autoClose"
    :data-triggers="triggersString"
    @open="onOpen"
    @close="onClose"
  >
    <template #content v-if="$slots.content">
      <slot name="content" />
    </template>
    <slot />
  </b-tooltip>
</template>

<script>
import { MobileTooltipPositioningMixin } from '@/modules/core/ui/mixins/MobileTooltipPositioningMixin';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'TooltipView',
  emits: ['close', 'open'],
  mixins: [MobileTooltipPositioningMixin],
  props: {
    active: {
      type: Boolean,
      default: true
    },
    type: String,
    label: String,
    delay: Number,
    closeDelay: Number,
    position: {
      type: String,
      default: 'is-top',
      validator(value) {
        return ['is-top', 'is-bottom', 'is-left', 'is-right'].includes(value);
      }
    },
    triggers: {
      type: Array,
      default: () => ['hover']
    },
    always: Boolean,
    square: Boolean,
    dashed: Boolean,
    multilined: Boolean,
    size: {
      type: String,
      default: 'is-medium'
    },
    appendToBody: Boolean,
    animated: {
      type: Boolean,
      default: true
    },
    animation: {
      type: String,
      default: 'fade'
    },
    contentClass: String,
    autoClose: {
      type: [Array, Boolean],
      default: true
    },
    closeOnTriggerClick: {
      type: Boolean,
      default: false
    },
    isVisibleInMobile: {
      type: Boolean,
      default: false
    },
    isInfo: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isTooltipActive: false
    };
  },
  computed: {
    triggersString() {
      return Array.isArray(this.triggers) ? this.triggers.join(',') : '';
    }
  },
  methods: {
    onClose(...args) {
      this.isTooltipActive = false;
      this.$emit('close', ...args);
    },
    onOpen(...args) {
      this.isTooltipActive = true;
      // Set data-triggers on tooltip-content element for mobile CSS targeting
      this.$nextTick(() => {
        const tooltipEl = this.$refs.tooltip?.$data?._bodyEl;
        if (tooltipEl) {
          tooltipEl.setAttribute('data-triggers', this.triggersString);

          // On mobile, control visibility and adjust position
          if (this.isMobile) {
            if (this.isVisibleInMobile && this.appendToBody) {
              // Show and adjust tooltip position to prevent viewport overflow
              this.adjustTooltipPositionForMobile(tooltipEl, this.$el);
            } else {
              // Hide tooltip on mobile if not explicitly enabled
              tooltipEl.style.display = 'none';
            }
          }
        }
      });
      this.$emit('open', ...args);
    },
    setZIndex(index) {
      setTimeout(() => {
        if (this.$refs.tooltip?.$data?._bodyEl) {
          this.$refs.tooltip.$data._bodyEl.style.zIndex = index;
        }
      }, 0);
    },
    setOpacity(opacity) {
      if (this.$refs.tooltip?.$data?._bodyEl) {
        this.$refs.tooltip.$data._bodyEl.style.opacity = opacity;
      }
    },
    setTop(top) {
      if (this.$refs.tooltip?.$data?._bodyEl) {
        this.$refs.tooltip.$data._bodyEl.style.top = top;
      }
    },
    getTop() {
      if (this.$refs.tooltip?.$el) {
        return this.$refs.tooltip.$el.getBoundingClientRect().top;
      }
      return 0;
    },
    close() {
      if (this.$refs.tooltip) {
        this.$refs.tooltip.$data.isActive = false;
        // Trigger Buefy's close handler to emit the close event
        this.onClose();
      }
    },
    shouldCloseOnOutsideClick() {
      // Close on outside click if autoClose is true or if array contains 'outside'
      if (this.autoClose === true) {
        return true;
      }

      if (Array.isArray(this.autoClose)) {
        return this.autoClose.includes('outside');
      }

      return false;
    },
    handleDocumentClick(event) {
      if (!this.isTooltipActive) {
        return;
      }

      const target = event.target;
      const triggerElement = this.$el;

      if (triggerElement?.contains(target)) {
        if (this.closeOnTriggerClick) {
          this.close();
        }
        return;
      }

      if (this.shouldCloseOnOutsideClick()) {
        this.close();
      }
    },
    handleScroll() {
      // Close tooltip when user scrolls
      if (this.isTooltipActive) {
        this.close();
      }
    }
  },
  mounted() {
    if (!this.isInfo) {
      return;
    }
    // Listen for document clicks to close tooltip when clicking on interactive elements
    document.addEventListener('click', this.handleDocumentClick, true);
    // Listen for scroll events to close tooltip when user scrolls
    window.addEventListener('scroll', this.handleScroll, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick, true);
    window.removeEventListener('scroll', this.handleScroll, true);
  }
};
</script>
